import {Component, CUSTOM_ELEMENTS_SCHEMA, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ProductChangeService} from '../../shared/service/product-change/product-change.service';
import {ErrorHandlingService} from '../../shared/service/error-handling/error-handling.service';
import {TranslateService, TranslatePipe} from '@ngx-translate/core';
import {Subject, Observable} from 'rxjs';
import {ErrorInfo} from '../../shared/model/error-info';
import {ProductChange} from '../../shared/model/product-change';
import {ProductChangeOrder} from '../../shared/service/product-change/product-change-order';
import {map, take} from 'rxjs/operators';
import {FilterService} from '../../shared/service/filter/filter.service';
import {environment} from '../../../../environments/environment';
import {ProductChangeResponse} from '../../shared/model/product-change-response';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {ErrorBoxComponent} from '../error-box/error-box.component';
import {AsyncPipe} from '@angular/common';
import {DacklFilterSingleSelectItem, DacklFilterSingleSelectComponent, DacklEmptyStateComponent, DacklAccordionComponent, DacklExpansionPanelComponent} from '@datev/ngx-dackl';
import {TargetBlankDirective} from 'commons';
import {MatAnchor} from '@angular/material/button';
import {FilterElement, ProductFilterElement} from '../../shared/model/filter';
import {ProductChangeCardComponent} from '../product-change-card/product-change-card.component';

import '@datev/dacks/content';
import { FooterSpacing } from "../footer/footer-spacing";

// Interface für gruppierte ProductChanges
interface GroupedProductChanges {
  productTitle: string;
  productChanges: ProductChange[];
}

@Component({
    selector: 'myupdates-product-change-selection',
    templateUrl: './product-change-selection.component.html',
    styleUrls: ['./product-change-selection.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [MatAnchor, MatIcon, TargetBlankDirective, DacklFilterSingleSelectComponent, DacklEmptyStateComponent, ErrorBoxComponent,
    MatProgressSpinner, AsyncPipe, TranslatePipe, ProductChangeCardComponent, DacklAccordionComponent, DacklExpansionPanelComponent, FooterSpacing]
})
export class ProductChangeSelectionComponent implements OnInit {

  groupedProductChanges$: Observable<GroupedProductChanges[]> = new Subject<GroupedProductChanges[]>();
  errorInfo$: Subject<ErrorInfo>;
  dateTranslation = '';
  dateTranslationMonth = '';
  sortOrderSelections: Array<DacklFilterSingleSelectItem> = [];
  sortOrderSelection: ProductChangeOrder;
  filterProductId = '';
  filterProductVersion = '';
  filterIris: string[] = [];
  filters: ProductFilterElement[] = [];
  productTitle = '';
  expandedPanelIds: string[] = [];

  protected readonly environment = environment;

  constructor(private readonly activatedRoute: ActivatedRoute,
              private readonly productChangeService: ProductChangeService,
              private readonly errorHandlingService: ErrorHandlingService,
              private readonly translate: TranslateService,
              private readonly filterService: FilterService) {

    this.sortOrderSelection = ProductChangeOrder.DeliveryDateDesc;
    this.errorInfo$ = errorHandlingService.errorStream$;
    const deliveryDateTranslationKey = 'product-change-cardview.sort_delivery_date';
    const publishingDateTranslationKey = 'product-change-cardview.sort_publishing_date';

    this.sortOrderSelections = [
      {label: this.translate.instant(publishingDateTranslationKey), value: ProductChangeOrder.PublishingDateDesc},
      {label: this.translate.instant(deliveryDateTranslationKey), value: ProductChangeOrder.DeliveryDateDesc}
    ];
    }

  ngOnInit(): void {
    this.initializeFromRoute();
    this.initializeTranslations();
    this.initializeFilters();

  }

  private loadAndProcessProductChanges(): void {
    let result: Observable<ProductChangeResponse>;

    if (this.filterIris.length > 0) {
      // IRI-basierte Suche hat Vorrang über product/version
      result = this.productChangeService.getProductChangesByIris(this.filterIris, this.sortOrderSelection);
    } else {
      // Standard produkt/version-basierte Suche
      result = this.productChangeService.getProductChangesByProductVersion(
        this.filterProductId, this.filterProductVersion, this.sortOrderSelection);
    }

    this.groupedProductChanges$ = this.errorHandlingService.bindErrorHandling(
      result.pipe(map((response: ProductChangeResponse) =>
        this.groupProductChangesByProductKey(response.productChanges.filter((item: ProductChange) => !!item.id))
      ))
    );
  }

  private groupProductChangesByProductKey(productChanges: ProductChange[]): GroupedProductChanges[] {
    // Erstelle eine Map zur Gruppierung nach ProductKey
    const grouped = new Map<string, ProductChange[]>();

    productChanges.forEach(pc => {
      const key = pc.productKey || 'Unbekannt';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(pc);
    });

    // Konvertiere Map zu Array und sortiere nach ProductTitle (alphabetisch)
    const groupedArray: GroupedProductChanges[] = Array.from(grouped.entries())
      .map(([productKey, changes]) => ({
        productTitle: this.getProductTitleForKey(productKey, changes),
        productChanges: changes
      }))
      .sort((a, b) => a.productTitle.localeCompare(b.productTitle));

    this.updateProductTitleBasedOnGroups(groupedArray); // Update der Titel-Logik

    return groupedArray;
  }

  private getProductTitleForKey(productKey: string, productChanges: ProductChange[]): string {
    // Ermittle ProductTitle aus den verfügbaren Filtern anhand des productKey
    const matchingProduct = this.filters.find(product => product.id === productKey);
    if (matchingProduct && matchingProduct.name) {
      return matchingProduct.name;
    }

    // 2. FALLBACK 1: ProductTitle aus den ProductChanges extrahieren
    if (productChanges.length > 0 && productChanges[0].title) {
      return productChanges[0].title;
    }

    // 3. FALLBACK 2: ProductKey als letzter Ausweg
    return productKey;
  }

  private buildProductTitleForIris(): string {
    if (this.filterIris.length === 0) {
      return '';
    }

    // Versuche Produkttitel aus den verfügbaren Filtern zu extrahieren
    const productNames: string[] = [];

    this.filterIris.forEach(iri => {
      // Suche nach passenden Produkten in den Filtern basierend auf der IRI
      const matchingProducts = this.filters.filter(product =>
        product.id.includes(iri) || iri.includes(product.id)
      );

      matchingProducts.forEach(product => {
        if (!productNames.includes(product.name)) {
          productNames.push(product.name);
        }
      });
    });

    if (productNames.length === 1) {
      return `zu ${productNames[0]}`;
    } else if (productNames.length > 1) {
      return `zu ${productNames.slice(0, -1).join(', ')} und ${productNames[productNames.length - 1]}`;
    } else {
      return 'für ausgewählte Produkte';
    }
  }

  private updateProductTitleBasedOnGroups(groupedProductChanges: GroupedProductChanges[]): void {
    if (groupedProductChanges.length > 1) {
      // Wenn mehrere unterschiedliche ProductKeys vorhanden sind, zeige nur "Programmänderungen"
      this.productTitle = '';
    } else if (groupedProductChanges.length === 1) {
      // Wenn nur ein ProductKey vorhanden ist, verwende den ProductTitle dieses Produkts
      this.productTitle = `zu ${groupedProductChanges[0].productTitle}`;
    }
    // Bei 0 Gruppen bleibt der ursprüngliche Titel bestehen (wird in ngOnInit gesetzt)
  }

  private initializeFromRoute(): void {
    const params = this.activatedRoute.snapshot.queryParams;
    const irisParam = params['iri'] ?? '';
    this.filterIris = irisParam ? irisParam.split(',').map((iri: string) => iri.trim()).filter((iri: string) => iri.length > 0) : [];
    this.filterProductId = params['product'] ?? '';
    this.filterProductVersion = params['version'] ?? '';
  }

  private initializeFilters(): void {
    this.filterService.getPossibleFilters().pipe(take(1)).subscribe(filters => {
      this.filters = filters;

      if (this.filterIris.length > 0) {
        // Für IRI-basierte Suche: Produkttitel aus den Filtern extrahieren
        this.productTitle = this.buildProductTitleForIris();
      } else {
        // Für produkt/version-basierte Suche
        const filterProduct = this.filters.find(product => product.id === this.filterProductId) as FilterElement;
        this.productTitle = filterProduct && filterProduct.name.length > 0 ? `zu ${filterProduct.name}` : '';
      }
      // Wichtig: ProductChanges erst laden/verarbeiten NACHDEM Filter verfügbar sind
      this.loadAndProcessProductChanges();
    });
  }

  private initializeTranslations(): void {
    this.dateTranslation = this.translate.instant('common.date_format');
    this.dateTranslationMonth = this.translate.instant('common.date_format_month');
  }

  sortOrderChanged(): void {
    this.productChangeService.sortOrderSelection = this.sortOrderSelection;
    // ProductChanges mit neuer Sortierung neu laden und gruppieren
    this.loadAndProcessProductChanges();
  }

  onPanelOpened(panelId: string): void {
    // Im Single-Panel-Modus: Schließe alle anderen Panels
    this.expandedPanelIds = [panelId];
  }

  onPanelClosed(panelId: string): void {
    this.expandedPanelIds = this.expandedPanelIds.filter(id => id !== panelId);
  }

  isPanelExpanded(panelId: string): boolean {
    return this.expandedPanelIds.includes(panelId);
  }
}
